import { FolderChildOutlined, FolderOpenOutlined, FolderOutlined } from '@vindr/icons';
import { isMobile } from '@vindr/utils/mobile';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useImmer } from 'use-immer';

/**
 * When using this Menu instance Antd?
 * @see https://ant.design/components/menu/
 *
 * + Custom click parent item
 * + Show title parent item
 */

export interface MenuProps {
  items: Array<{
    label: string;
    title?: string;
    type?: 'filter_id' | 'institution_id' | 'all';
    key: string;
    onClick?: () => void;
    children?: MenuProps['items'];
  }>;
  defaultOpenKeys?: string[];
  onClick?: (item: MenuProps['items'][0]) => void;
  onOpenChange?: (key: string) => void;
  defaultSelected?: string;
  className?: string;
}

const findDeepKey = (items: MenuProps['items'], key: string) => {
  let result = null;

  for (const item of items) {
    if (item.key === key) {
      result = item;
      break;
    } else if (item.children) {
      result = findDeepKey(item.children, key);
      if (result) {
        break;
      }
    }
  }

  return result;
};

const Menu = ({
  items,
  defaultOpenKeys,
  onClick,
  onOpenChange,
  defaultSelected,
  className,
}: MenuProps): JSX.Element => {
  const [openKeys, setOpenKeys] = useImmer(() => defaultOpenKeys);
  const [selected, setSelected] = useState<string>(defaultSelected || '');

  useEffect(() => {
    setOpenKeys(() => defaultOpenKeys);
  }, [defaultOpenKeys, setOpenKeys]);

  useEffect(() => {
    setSelected(defaultSelected);

    if (defaultSelected && typeof onClick === 'function' && items?.length > 0) {
      const item = findDeepKey(items, defaultSelected);
      if (!item) {
        setSelected('');
        return;
      }
      onClick(item);
    }
  }, [defaultSelected, items, onClick]);

  const onOpenChangeHandler = useCallback(
    (key: string) => {
      setOpenKeys((draft) => {
        const index = draft.indexOf(key);

        if (index > -1) {
          draft.splice(index, 1);
        } else {
          draft.push(key);
        }
      });

      onOpenChange?.(key);
    },
    [onOpenChange, setOpenKeys]
  );

  const onClickHandler = useCallback(
    (item: MenuProps['items'][0]) => {
      onClick?.(item);
    },
    [onClick]
  );

  return (
    <div
      className={twMerge('h-full overflow-y-auto overflow-x-hidden p-1 py-[12px]', className)}
      style={{
        borderInlineEnd: '1px solid rgba(154, 218, 251, 0.39)',
      }}
    >
      {items?.length && defaultOpenKeys ? (
        items.map((item, index) => (
          <div key={item.key}>
            <MenuParent
              item={item}
              openKeys={openKeys}
              onOpenChange={onOpenChangeHandler}
              onClick={onClickHandler}
              selected={selected}
              setSelected={setSelected}
            />
          </div>
        ))
      ) : (
        <MenuSkeleton />
      )}
    </div>
  );
};

const MenuParent = ({
  item,
  openKeys,
  onOpenChange,
  onClick,
  selected,
  setSelected,
}: {
  item: MenuProps['items'][0];
  openKeys: MenuProps['defaultOpenKeys'];
  onOpenChange: MenuProps['onOpenChange'];
  onClick: MenuProps['onClick'];
  selected: string;
  setSelected: (key: string) => void;
}): JSX.Element => {
  return (
    <>
      <ChildrenWrapper
        selected={selected}
        item={item}
        onClick={() => {
          setSelected(item.key);
          onClick(item);
        }}
      >
        <Arrow
          openKeys={openKeys}
          item={item}
          selected={selected}
          onOpenChange={(e) => {
            e.stopPropagation();
            e.preventDefault();

            if (item.children) {
              onOpenChange(item.key);
            }
          }}
          className={`${item.children ? '' : 'opacity-0'}`}
          hasChildren={!!item.children}
        />
        <div className="flex items-center truncate">
          <Title selected={selected} item={item} />
        </div>
      </ChildrenWrapper>
      {item.children && openKeys.includes(item.key) && (
        <div className="pl-4">
          {item.children.map((child) => (
            <MenuChild
              key={child.key}
              item={child}
              onClick={onClick}
              openKeys={openKeys}
              onOpenChange={onOpenChange}
              selected={selected}
              setSelected={setSelected}
              className="h-[34px]"
            />
          ))}
        </div>
      )}
    </>
  );
};

const MenuChild = ({
  item,
  onClick,
  onOpenChange,
  openKeys,
  selected,
  setSelected,
  className,
}: {
  item: MenuProps['items'][0];
  onClick: MenuProps['onClick'];
  onOpenChange: MenuProps['onOpenChange'];
  openKeys: MenuProps['defaultOpenKeys'];
  selected: string;
  setSelected: (key: string) => void;
  className: string;
}) => {
  if (item.children) {
    return (
      <MenuParent
        item={item}
        onClick={onClick}
        onOpenChange={onOpenChange}
        openKeys={openKeys}
        selected={selected}
        setSelected={setSelected}
      />
    );
  }

  return (
    <ChildrenWrapper
      selected={selected}
      item={item}
      onClick={() => {
        onClick(item);
        setSelected(item.key);
      }}
      className={className}
    >
      <div className="flex items-center truncate">
        <FolderChildOutlined className="px-2" />
        <Title item={item} selected={selected} />
      </div>
    </ChildrenWrapper>
  );
};

const Title = ({ selected, item }: { selected: string; item: MenuProps['items'][0] }) => {
  return (
    <p
      className={classNames('mb-0 select-none truncate text-[12px] leading-4', {
        'text-primary-light': selected === item.key,
        'text-black dark:text-white': selected !== item.key,
      })}
    >
      {item.label}
    </p>
  );
};

const ChildrenWrapper = ({
  children,
  selected,
  item,
  className,
  ...props
}: {
  children: React.ReactNode;
  selected: string;
  item: MenuProps['items'][0];
  [key: string]: unknown;
  className?: string;
}) => {
  const selectedItemRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Scroll the selected item into view when it changes
    if (selectedItemRef.current && !isMobile()) {
      setTimeout(
        () => selectedItemRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        0
      );
    }
  }, [selected]);

  return (
    <div
      ref={selected === item.key ? selectedItemRef : null}
      className={classNames(
        twMerge(
          'flex h-[34px] cursor-pointer scroll-m-4 items-center py-[8px] pr-[8px] hover:bg-[#eaeaea] dark:hover:bg-[#ffffff1f]',
          className
        ),
        {
          'border-r-2 border-primary-light bg-primary-lighter dark:bg-primary-dark':
            selected === item.key,
        }
      )}
      {...props}
      title={item.title}
    >
      {children}
    </div>
  );
};

const Arrow = ({
  openKeys,
  item,
  selected,
  className,
  onOpenChange,
  hasChildren,
}: {
  openKeys: MenuProps['defaultOpenKeys'];
  item: MenuProps['items'][0];
  selected: string;
  onOpenChange?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  className?: string;
  hasChildren: boolean;
}) => {
  return (
    <div onClick={onOpenChange} className="px-2">
      <div className="relative flex">
        {hasChildren && openKeys.includes(item.key) && (
          <FolderOpenOutlined className="text-[17px]" />
        )}
        {hasChildren && !openKeys.includes(item.key) && <FolderOutlined className="text-[14px]" />}
        {!hasChildren && <FolderChildOutlined />}
      </div>
    </div>
  );
};

export default Menu;

const MenuSkeleton = () => (
  <div role="status" className="max-w-sm animate-pulse">
    <div className="mb-5 mt-[10px] h-2.5 w-1/4 rounded-full bg-gray-200 dark:bg-gray-700" />
    <div className="my-5 h-2.5 w-1/4 rounded-full bg-gray-200 dark:bg-gray-700" />
    <div className="my-5 ml-4 h-2 w-1/3 rounded-full bg-gray-200 dark:bg-gray-700" />
    <div className="my-5 ml-4 h-2 w-1/2 rounded-full bg-gray-200 dark:bg-gray-700" />
    <div className="my-5 h-2.5 w-1/6 rounded-full bg-gray-200 dark:bg-gray-700" />
    <div className="ml-4 h-2 w-1/3 rounded-full bg-gray-200 dark:bg-gray-700" />
    <div className="my-5 ml-4 h-2 w-1/2 rounded-full bg-gray-200 dark:bg-gray-700" />
    <div className="my-5 ml-4 h-2 w-1/2 rounded-full bg-gray-200 dark:bg-gray-700" />
    <span className="sr-only">Loading...</span>
  </div>
);
